import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@heroui/react';
import type { RosterUser, ShiftResponse } from '@/api/models';
import { useDeviceRosterQuery, useVerifyPinMutation } from '@/api/queries/auth';
import { useExpectedCashQuery } from '@/api/queries/shifts';
import {
    apiErrorMessage,
    APPROVAL_REQUIRED,
    APPROVER_INVALID,
    getUser,
} from '@/utils';
import { PIN_LENGTH } from '@/components/pinPad/pinPadMeta';
import {
    pinAttemptMessage,
    usePinAttemptError,
    type PinAttemptError,
    type PinErrorMessage,
} from '@/components/pinPad/usePinAttemptError';
import { useShift } from './useShift';
import {
    classifyDiff,
    computeDiff,
    type DiffKind,
    type ShiftFlowMode,
    type ShiftStep,
    type ShiftStepAction,
} from './shiftMeta';

/** What makes up the expected cash at close, as the server computed it. */
export type ShiftCashBreakdown = {
    openingFloat: number;
    cashSales: number;
    cashRefunds: number;
    payouts: number;
};

/** A finished close as the server recorded it, not as the client computed it. */
export type ShiftCloseSummary = {
    expected: number;
    counted: number;
    difference: number;
    diffKind: DiffKind;
};

export type ShiftFlowValue = {
    /** Which action the button + modal perform, derived from the shift status. */
    mode: ShiftFlowMode;

    /* Modal */
    isModalOpen: boolean;
    openModal: () => void;
    closeModal: () => void;

    /* Step */
    step: ShiftStep;
    goToApprove: () => void;
    backToCount: () => void;

    /* Blind count */
    counted: number | null;
    /** Ignored while `isCountLocked`: a confirmed count changes only through `recount`. */
    setCounted: (value: number | null) => void;
    /**
     * The seller confirmed the count, so they have seen (or are about to see)
     * the expected cash. The count they committed to holds until `recount`
     * or the dialog closes; otherwise it could be edited into a match after
     * the reveal and skip the co-sign (SEL-48).
     */
    isCountLocked: boolean;
    /**
     * The count is shown against the expected cash: the seller confirmed it and
     * the latest expected-cash read succeeded. A failed refetch hides it again,
     * so a count is never judged against a figure the server didn't just send.
     */
    revealed: boolean;
    /**
     * Refetches the expected cash and reveals the count against the fresh
     * value. A failed refetch leaves the count hidden.
     */
    reveal: () => Promise<void>;
    /** `true` while the reveal waits for the fresh expected cash. */
    isRevealing: boolean;
    recount: () => void;

    /* Reveal (derived) — `expected` is server-authoritative */
    expected: number;
    /** The close-side parts of `expected`; `null` for an open. */
    breakdown: ShiftCashBreakdown | null;
    diff: number;
    diffKind: DiffKind;
    canStart: boolean;
    needsApproval: boolean;

    /* Admin co-sign (discrepancy path) */
    approvers: RosterUser[];
    approversLoading: boolean;
    approversError: boolean;
    selectedApprover: RosterUser | null;
    /**
     * The approver the picker should focus when it appears: the one the seller
     * just backed out of, if still listed, otherwise the first. `null` only
     * when there is nobody to focus.
     */
    focusApproverId: string | null;
    selectApprover: (user: RosterUser) => void;
    approverPin: string;
    onPinDigit: (digit: string) => void;
    onPinBackspace: () => void;
    /** `Esc` on the co-sign PIN: clears a non-empty entry, else returns to the approver picker. */
    onPinEscape: () => void;
    approved: boolean;
    approvalError: PinAttemptError | null;
    approvalErrorMessage: PinErrorMessage | null;
    /** The server asked us to wait: the pad takes no input until it ends. */
    isApprovalHeld: boolean;
    isVerifying: boolean;

    /* Submit */
    canSubmit: boolean;
    isSubmitting: boolean;
    /** Ignored while an open or close is already in flight (SEL-49). */
    submit: () => void;

    /* Step action: the footer's primary button and `Enter` both run it */
    stepAction: ShiftStepAction;
    /** `false` while the step has nothing to act on yet: no count, or no co-sign. */
    canConfirmStep: boolean;
    /** The step's action is waiting on the server: the fresh expected cash, or the open/close. */
    isConfirmingStep: boolean;
    /** Runs `stepAction`; a no-op while `canConfirmStep` is `false`. */
    confirmStep: () => void;

    /* After close */
    closeSummary: ShiftCloseSummary | null;
};

const APPROVAL_PIN_ERROR_KEYS = {
    lockout: 'seller.shift.approverLocked',
    rateLimited: 'seller.shift.pinRateLimited',
    invalid: 'seller.shift.invalidPin',
};

/**
 * Drives the open/close shift flow against the live API. `expected` comes from
 * `GET /shifts/expected` (fetched when the modal opens, still hidden behind the
 * blind count until `reveal()`). A matching count starts directly. A discrepancy
 * advances to an approver step: an admin (a BranchAdmin who isn't the current
 * seller) co-signs with their PIN — pre-checked via `/auth/pin/verify` for
 * instant button feedback, then re-verified authoritatively by the server on
 * `POST /shifts/open|close`. `submit` is async; `isSubmitting` tracks the
 * mutation. A server rejection keeps the modal open so the operator can retry.
 *
 * The server's figures win (SEL-29). The reveal waits for a fresh expected
 * cash, and a failed submit refetches it. `APPROVAL_REQUIRED` moves to the
 * approver step even when the client thought the count matched. A finished
 * close shows the difference and expected cash the server recorded.
 */
export const useShiftFlow = (): ShiftFlowValue => {
    const { t } = useTranslation('pages');
    const { status, statusRead, canOpenShift, openShift, closeShift } =
        useShift();
    const mode: ShiftFlowMode = status === 'open' ? 'close' : 'open';

    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [step, setStep] = React.useState<ShiftStep>('count');
    const [counted, setCounted] = React.useState<number | null>(null);
    // The seller confirmed the count; `revealed` below also needs the figure.
    const [revealRequested, setRevealed] = React.useState(false);
    const [isRevealing, setIsRevealing] = React.useState(false);
    // The server refused the count for want of a co-sign. It knows the expected
    // cash better than this client, so its word holds until a re-count.
    const [serverNeedsApproval, setServerNeedsApproval] = React.useState(false);
    const [selectedApprover, setSelectedApprover] =
        React.useState<RosterUser | null>(null);
    // Kept across a trip to the PIN and back, so the picker refocuses the same
    // approver instead of resetting to the first (the roster's own rule).
    const [lastPickedApproverId, setLastPickedApproverId] = React.useState<
        string | null
    >(null);
    const [approverPin, setApproverPin] = React.useState('');
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    // The synchronous side of `isSubmitting` (rulebook §13): a second submit
    // in the same render, before the state catches up, still sees it.
    const isSubmitLocked = React.useRef(false);
    const [closedShift, setClosedShift] = React.useState<ShiftResponse | null>(
        null,
    );

    // Counting starts over on dismissal and re-count. A reveal or submit that
    // settles after one of those belongs to a flow the seller already left.
    const flowGeneration = React.useRef(0);

    // Both reads only matter once the modal is open; gate the fetches so an idle
    // POS doesn't poll them. A finished close shows the server's record, and the
    // mode has flipped to `open` by then, so nothing needs the expected cash.
    const roster = useDeviceRosterQuery(isModalOpen);
    const expectedQuery = useExpectedCashQuery(
        mode,
        isModalOpen && step !== 'closed',
    );
    const verify = useVerifyPinMutation();

    const currentUserId = getUser()?.id ?? '';

    const approvers = React.useMemo<RosterUser[]>(() => {
        const items =
            roster.data && roster.data.success
                ? roster.data.response.items
                : [];
        // A co-sign is a SECOND person: only branch admins, never the seller
        // who's running the till.
        return items.filter(
            (user) => user.role === 'BranchAdmin' && user.id !== currentUserId,
        );
    }, [roster.data, currentUserId]);
    const approversLoading = roster.isLoading;
    const focusApproverId = React.useMemo(() => {
        if (
            lastPickedApproverId &&
            approvers.some((user) => user.id === lastPickedApproverId)
        ) {
            return lastPickedApproverId;
        }
        return approvers[0]?.id ?? null;
    }, [lastPickedApproverId, approvers]);
    const approversError =
        roster.isError || (roster.data ? !roster.data.success : false);

    // The reveal is gated on the latest read succeeding, not just on having
    // asked: a failed fetch (a refetch after a refused submit, say) must not let
    // `expected` read 0 and turn every count into a false discrepancy.
    const expectedEnvelope = expectedQuery.data;
    const expectedReady = expectedEnvelope?.success === true;
    const revealed = revealRequested && expectedReady;
    const expected =
        expectedEnvelope && expectedEnvelope.success
            ? expectedEnvelope.response.expected
            : 0;
    const breakdown = React.useMemo<ShiftCashBreakdown | null>(() => {
        if (!expectedEnvelope?.success) {
            return null;
        }
        const { type, openingFloat, cashSales, cashRefunds, payouts } =
            expectedEnvelope.response;
        return type === 'close'
            ? {
                  openingFloat: openingFloat ?? 0,
                  cashSales: cashSales ?? 0,
                  cashRefunds: cashRefunds ?? 0,
                  payouts: payouts ?? 0,
              }
            : null;
    }, [expectedEnvelope]);
    const refetchExpected = expectedQuery.refetch;

    const approved = verify.data?.success === true;
    const approvalError = usePinAttemptError(verify.data);
    const approvalErrorMessage = pinAttemptMessage(
        approvalError,
        APPROVAL_PIN_ERROR_KEYS,
    );
    const isRateLimited = approvalError?.kind === 'rateLimited';
    const isVerifying = verify.isPending;

    const resetApproval = React.useCallback(() => {
        setSelectedApprover(null);
        setApproverPin('');
        verify.reset();
    }, [verify]);

    // Nothing starts until `GET /shifts/current` has answered successfully:
    // until then, loading or failed, `mode` reads `open` whatever the server
    // holds (SEL-49). The handover guard (SEL-30): another seller's shift is
    // still open in this device's drawer, so the open flow doesn't start.
    // Closing is never held.
    const isStatusKnown = statusRead.state === 'known';
    const openModal = React.useCallback(() => {
        if (!isStatusKnown || (mode === 'open' && !canOpenShift)) {
            return;
        }
        setIsModalOpen(true);
    }, [isStatusKnown, mode, canOpenShift]);

    const closeModal = React.useCallback(() => {
        flowGeneration.current += 1;
        setIsModalOpen(false);
        setStep('count');
        setCounted(null);
        setRevealed(false);
        setIsRevealing(false);
        setServerNeedsApproval(false);
        setClosedShift(null);
        setLastPickedApproverId(null);
        resetApproval();
    }, [resetApproval]);

    // Sales keep landing while the seller counts, so the figure fetched when
    // the modal opened may be stale: reveal only against a fresh one. A failed
    // refetch keeps the count hidden rather than judging it against old data.
    const reveal = React.useCallback(async () => {
        if (counted === null || isRevealing) {
            return;
        }
        const generation = flowGeneration.current;
        setIsRevealing(true);
        try {
            const fresh = await refetchExpected();
            if (generation !== flowGeneration.current) {
                return;
            }
            if (fresh.isSuccess && fresh.data.success) {
                setRevealed(true);
            } else {
                toast.danger(
                    fresh.data && !fresh.data.success
                        ? apiErrorMessage(fresh.data.error, t)
                        : t('seller.shift.expectedRefreshError'),
                );
            }
        } finally {
            if (generation === flowGeneration.current) {
                setIsRevealing(false);
            }
        }
    }, [counted, isRevealing, refetchExpected, t]);

    // The lock follows the seller's confirmation, not `revealed`: a failed
    // refetch hides the figures again, but the seller has already seen them.
    const isCountLocked = revealRequested;
    const changeCounted = React.useCallback(
        (value: number | null) => {
            if (isCountLocked) {
                return;
            }
            setCounted(value);
        },
        [isCountLocked],
    );

    // Re-count keeps the entered amount so the seller can nudge it, but returns
    // to the count step and clears any pending co-sign. It is the only way to
    // change a confirmed count.
    const recount = React.useCallback(() => {
        flowGeneration.current += 1;
        setRevealed(false);
        setIsRevealing(false);
        setStep('count');
        setServerNeedsApproval(false);
        resetApproval();
    }, [resetApproval]);

    const goToApprove = React.useCallback(() => setStep('approve'), []);

    const backToCount = React.useCallback(() => {
        setStep('count');
        resetApproval();
    }, [resetApproval]);

    const selectApprover = React.useCallback(
        (user: RosterUser) => {
            setSelectedApprover(user);
            setLastPickedApproverId(user.id);
            setApproverPin('');
            verify.reset();
        },
        [verify],
    );

    const onPinDigit = React.useCallback(
        (digit: string) => {
            if (!selectedApprover || verify.isPending || isRateLimited) {
                return;
            }
            // Tapping after a failed attempt starts a clean entry: a failure
            // leaves the pad full (4 dots), so restart from empty rather than
            // wedging at PIN_LENGTH where no digit can be appended.
            const afterFailure = !!verify.data && !verify.data.success;
            if (afterFailure) {
                verify.reset();
            }
            const base = afterFailure ? '' : approverPin;
            if (base.length >= PIN_LENGTH) {
                return;
            }
            const next = base + digit;
            setApproverPin(next);
            // Auto pre-check on the last digit, against the chosen approver.
            // Called here, not inside a state updater, which StrictMode runs
            // twice (SEL-49). A refusal clears the entry (bos-seller#61) so the
            // next attempt starts from empty; the refusal's message stays
            // until then.
            if (next.length === PIN_LENGTH) {
                verify.mutate(
                    { userId: selectedApprover.id, pin: next },
                    {
                        onSuccess: (result) => {
                            if (!result.success) {
                                setApproverPin('');
                            }
                        },
                    },
                );
            }
        },
        [selectedApprover, verify, isRateLimited, approverPin],
    );

    const onPinBackspace = React.useCallback(() => {
        if (verify.isPending || isRateLimited) {
            return;
        }
        if (verify.data && !verify.data.success) {
            verify.reset();
        }
        setApproverPin((current) => current.slice(0, -1));
    }, [verify, isRateLimited]);

    // Layered like the sign-in PIN's `Esc`: clear a non-empty entry, or back
    // out to the picker on an empty one. Blocked mid-verify and mid-submit;
    // still allowed while rate-limited, since backing out needs no server.
    const onPinEscape = React.useCallback(() => {
        if (verify.isPending || isSubmitting) {
            return;
        }
        if (approverPin.length > 0) {
            setApproverPin('');
            verify.reset();
            return;
        }
        setSelectedApprover(null);
        verify.reset();
    }, [verify, isSubmitting, approverPin]);

    const diff = computeDiff(expected, counted ?? 0);
    const diffKind = classifyDiff(expected, counted ?? 0);

    const canStart = revealed && diffKind === 'match' && !serverNeedsApproval;
    const needsApproval =
        revealed && (diffKind !== 'match' || serverNeedsApproval);
    const canSubmit = canStart || (needsApproval && approved);

    const submit = React.useCallback(async () => {
        if (!canSubmit || counted === null || isSubmitLocked.current) {
            return;
        }
        // Held until this post settles, whatever the seller does meanwhile:
        // two `Enter`s, or `Enter` and a click, post once (SEL-49).
        isSubmitLocked.current = true;
        // On a discrepancy the server re-verifies this co-sign authoritatively.
        const approval =
            needsApproval && selectedApprover
                ? { approverUserId: selectedApprover.id, pin: approverPin }
                : undefined;
        const generation = flowGeneration.current;
        setIsSubmitting(true);
        try {
            const result =
                mode === 'open'
                    ? await openShift({ countedCash: counted, approval })
                    : await closeShift({ countedCash: counted, approval });
            // The seller may have dismissed or re-counted while this ran; the
            // toast still reports the outcome, but the flow they left stays gone.
            const isCurrentFlow = generation === flowGeneration.current;
            if (result.success) {
                toast.success(
                    t(
                        mode === 'open'
                            ? 'seller.shift.openedToast'
                            : 'seller.shift.closedToast',
                    ),
                );
                if (!isCurrentFlow) {
                    return;
                }
                if (mode === 'open') {
                    closeModal();
                } else {
                    // Show what the server booked: it can differ from the
                    // reveal when a sale landed in between.
                    setClosedShift(result.response);
                    setStep('closed');
                }
                return;
            }

            // Keep the modal open so the operator can re-count / retry.
            // SEL-19: code-driven, so each refusal reads as its own localized
            // message rather than as raw server prose.
            toast.danger(apiErrorMessage(result.error, t));
            if (!isCurrentFlow) {
                return;
            }

            // Whatever the refusal, the figure the count was judged against
            // may be what moved. Until the refetch succeeds `revealed` stays
            // false, so the count is never shown against a failed read.
            const code = result.error?.code;
            if (code === APPROVAL_REQUIRED) {
                // The server's word decides, even when this client's count
                // matched: move to the co-sign once the fresh figure is in.
                const fresh = await refetchExpected();
                if (generation !== flowGeneration.current) {
                    return;
                }
                setServerNeedsApproval(true);
                if (fresh.isSuccess && fresh.data.success) {
                    setStep('approve');
                } else {
                    // Back to the hidden count: confirming it again refetches,
                    // and the co-sign is still required after that.
                    setRevealed(false);
                    setStep('count');
                    toast.danger(t('seller.shift.expectedRefreshError'));
                }
                return;
            }
            void refetchExpected();
            if (code === APPROVER_INVALID) {
                // Same approver, clean pad: the seller re-enters the PIN or
                // picks someone else.
                setApproverPin('');
                verify.reset();
            }
        } finally {
            isSubmitLocked.current = false;
            setIsSubmitting(false);
        }
    }, [
        canSubmit,
        counted,
        needsApproval,
        selectedApprover,
        approverPin,
        mode,
        openShift,
        closeShift,
        closeModal,
        refetchExpected,
        verify,
        t,
    ]);

    // The one step-to-action mapping (SEL-49): the footer's primary button
    // shows it and `Enter` runs it, so the two can't drift apart.
    const submitAction: ShiftStepAction =
        mode === 'open' ? 'openShift' : 'closeShift';
    const stepAction: ShiftStepAction =
        step === 'closed'
            ? 'done'
            : step === 'approve'
              ? submitAction
              : !revealed
                ? 'confirmCount'
                : needsApproval
                  ? 'getApproval'
                  : submitAction;
    const isSubmitStep = stepAction === submitAction;
    const canConfirmStep =
        stepAction === 'confirmCount'
            ? counted !== null
            : isSubmitStep
              ? canSubmit
              : true;
    const isConfirmingStep =
        stepAction === 'confirmCount'
            ? isRevealing
            : isSubmitStep && isSubmitting;

    const confirmStep = React.useCallback(() => {
        if (!canConfirmStep) {
            return;
        }
        if (stepAction === 'confirmCount') {
            void reveal();
        } else if (stepAction === 'getApproval') {
            goToApprove();
        } else if (stepAction === 'done') {
            closeModal();
        } else {
            void submit();
        }
    }, [canConfirmStep, stepAction, reveal, goToApprove, closeModal, submit]);

    const closeSummary = React.useMemo<ShiftCloseSummary | null>(
        () =>
            closedShift
                ? {
                      expected: closedShift.expectedAtClose ?? 0,
                      counted: closedShift.closingCount ?? 0,
                      difference: closedShift.closeDiscrepancy ?? 0,
                      diffKind: classifyDiff(
                          closedShift.expectedAtClose ?? 0,
                          closedShift.closingCount ?? 0,
                      ),
                  }
                : null,
        [closedShift],
    );

    return React.useMemo(
        () => ({
            mode,
            isModalOpen,
            openModal,
            closeModal,
            step,
            goToApprove,
            backToCount,
            counted,
            setCounted: changeCounted,
            isCountLocked,
            revealed,
            reveal,
            isRevealing,
            recount,
            expected,
            breakdown,
            diff,
            diffKind,
            canStart,
            needsApproval,
            approvers,
            approversLoading,
            approversError,
            selectedApprover,
            focusApproverId,
            selectApprover,
            approverPin,
            onPinDigit,
            onPinBackspace,
            onPinEscape,
            approved,
            approvalError,
            approvalErrorMessage,
            isApprovalHeld: isRateLimited,
            isVerifying,
            canSubmit,
            isSubmitting,
            submit,
            stepAction,
            canConfirmStep,
            isConfirmingStep,
            confirmStep,
            closeSummary,
        }),
        [
            mode,
            isModalOpen,
            openModal,
            closeModal,
            step,
            goToApprove,
            backToCount,
            counted,
            changeCounted,
            isCountLocked,
            revealed,
            reveal,
            isRevealing,
            recount,
            expected,
            breakdown,
            diff,
            diffKind,
            canStart,
            needsApproval,
            approvers,
            approversLoading,
            approversError,
            selectedApprover,
            focusApproverId,
            selectApprover,
            approverPin,
            onPinDigit,
            onPinBackspace,
            onPinEscape,
            approved,
            approvalError,
            approvalErrorMessage,
            isRateLimited,
            isVerifying,
            canSubmit,
            isSubmitting,
            submit,
            stepAction,
            canConfirmStep,
            isConfirmingStep,
            confirmStep,
            closeSummary,
        ],
    );
};
